import { z } from 'zod';
import {
  custoUnitarioSchema,
  dataDoFatoSchema,
  dataHoraSchema,
  dataSchema,
  dinheiroSchema,
  paginacaoSchema,
  quantidadeSchema,
  respostaListaSchema,
  uuidSchema,
} from './comum.ts';

/**
 * Lançamento de consumo (`.spec/010`, "Lançamento de Consumo"; `.spec/016`,
 * seção 9).
 *
 * É a **saída de material para uma obra** — o que o lançamento de estoque
 * (`estoque.ts`) não sabe dizer. A diferença entre os dois cabe na obra, que
 * aqui é obrigatória: consumo é material que virou custo de obra, e sem a obra o
 * movimento é uma baixa qualquer, que o lançamento de estoque já cobre.
 *
 * ## Documento com itens, desde 12/09/2026
 *
 * O consumo nasceu como **uma linha**: um insumo, uma quantidade, um documento.
 * Quem desce ao almoxarifado não retira um material — retira a lista da
 * concretagem, com cimento, areia, brita e arame —, e o desenho de uma linha
 * obrigava a repetir obra, depósito, data e tipo de movimentação quinze vezes
 * para registrar uma retirada só. O que se perdia não era digitação: era o
 * documento. Quinze lançamentos soltos não se conferem juntos, não se excluem
 * juntos e não se imprimem como a requisição que o encarregado assinou.
 *
 * A forma é a da **lista de compra direta** (`listas-compra-direta.ts`), e a
 * simetria é proposital: os dois são "a obra pede, alguém atende", um antes da
 * compra e outro depois dela.
 *
 * **O que é do cabeçalho e o que é do item.** No cabeçalho fica o que vale para a
 * retirada inteira — empresa, data, tipo de movimentação, obra e depósito. No
 * item fica o que muda linha a linha: o material, a quantidade, a observação e a
 * **etapa da obra**. A etapa é do item porque a mesma retirada alimenta a laje do
 * 3º e a alvenaria do 2º no mesmo carrinho, e um campo único de cabeçalho
 * obrigaria a abrir dois documentos para o mesmo gesto.
 *
 * O arquivo é separado de `estoque.ts` de propósito, apesar de as tabelas
 * viverem no mesmo schema Drizzle. Juntá-los faria um módulo só descrever dois
 * recursos com permissões diferentes — `controladoria.estoque.*` e `consumo.*`
 * (`auth.ts`) —, e o primeiro `import` da tela de consumo arrastaria a metade
 * que ela não usa.
 */

/* -------------------------------------------------------------------------- */
/* Origem                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * De onde o consumo veio.
 *
 * `NOTA` é o **consumo automático** (`.spec/010`, "Movimentação de estoque"): o
 * insumo marcado com "consumo automático" na empresa sai do mesmo depósito em
 * que entrou, no instante da confirmação da nota. No banco isso é
 * `nota_compra_item_id` preenchido, e não uma coluna de origem.
 *
 * A origem é **do cabeçalho**, e não do item: o documento inteiro veio da nota ou
 * foi lançado à mão, e é ele que se exclui. O consumo automático nasce com **um
 * item só** — cada fatia do rateio da nota gera a sua retirada —, e isso não é
 * exceção ao desenho: é o caso de uma linha do documento que aceita muitas.
 *
 * A origem é **derivada e devolvida resolvida** em vez de o cliente inferi-la de
 * um campo nulo: a tela precisa dela em três lugares — a coluna da listagem, o
 * botão de excluir e a mensagem de recusa —, e três leituras de "id nulo
 * significa manual" divergiriam na primeira mudança.
 */
export const ORIGENS_CONSUMO = ['MANUAL', 'NOTA'] as const;
export const origemConsumoSchema = z.enum(ORIGENS_CONSUMO);
export type OrigemConsumo = z.infer<typeof origemConsumoSchema>;

export const ROTULO_ORIGEM_CONSUMO: Readonly<Record<OrigemConsumo, string>> = {
  MANUAL: 'Lançado à mão',
  NOTA: 'Nota de compra',
};

/**
 * O consumo pode ser excluído?
 *
 * Vive no contrato porque a tela e a API precisam da **mesma** regra: o
 * formulário decide se mostra o botão de excluir, e o service decide se recusa a
 * requisição. Duas cópias divergiriam, e a divergência apareceria como botão que
 * o servidor recusa — o pior dos dois defeitos, porque o usuário já clicou.
 *
 * O consumo automático não se exclui aqui: quem o gerou foi a confirmação da
 * nota, e quem o desfaz é a exclusão dela, que reverte estoque, saldo, custo
 * médio e contas a pagar na mesma transação (`.spec/010`, "Exclusão de Nota de
 * Compra confirmada"). Apagá-lo por esta rota deixaria a nota confirmada
 * apontando para um consumo que não existe, e a reversão futura não teria o que
 * desfazer — sem erro nenhum aparecer.
 */
export function consumoExcluivel(origem: OrigemConsumo): boolean {
  return origem === 'MANUAL';
}

/* -------------------------------------------------------------------------- */
/* Itens                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Teto de itens por consumo.
 *
 * Sanidade, não regra de negócio — o mesmo número da lista de compra direta, e
 * pelo mesmo motivo: uma requisição de canteiro tem dez ou vinte linhas, e quem
 * passar de duzentas quase certamente colou uma planilha inteira. O limite
 * transforma isso em erro em vez de num documento que ninguém consegue conferir
 * — e num lançamento que trava duzentas linhas de saldo na mesma transação.
 */
export const MAXIMO_ITENS_CONSUMO = 200;

/**
 * Quantidade consumida: **sempre positiva**.
 *
 * Não existe consumo negativo. Devolver material da obra ao depósito é a
 * **devolução de consumo** (`devolucoes-consumo.ts`, decisão 82), que aponta a
 * linha devolvida e tira o valor dela do custo da obra. Até 26/09/2026 este
 * comentário mandava a devolução para o lançamento de estoque, e o material
 * voltava ao depósito deixando o custo na obra. Aceitar o sinal aqui criaria um
 * terceiro jeito de dar entrada, invisível na tela de estoque.
 */
const quantidadeDoConsumoSchema = quantidadeSchema.refine(
  (v) => Number(v) > 0,
  'A quantidade consumida deve ser maior que zero.',
);

/**
 * Um item do consumo, como o cliente o manda.
 *
 * **Três ausências, e as três são a mesma decisão: o servidor é quem sabe.**
 *
 * - `valorUnitario` é o custo médio vigente no depósito no instante da baixa. A
 *   saída **consome** o custo médio, não o forma (`.spec/010`); recebê-lo faria
 *   o valor do documento contradizer a baixa que o saldo sofreu. É a diferença
 *   em relação ao lançamento de estoque, onde o campo existe porque a entrada
 *   pode formar custo.
 * - `valorTotal` é `quantidade × custo médio`. Recebê-lo permitiria um total que
 *   não confere com os fatores.
 * - `depositoId` é do cabeçalho: a retirada inteira é de um almoxarifado. Um
 *   depósito por linha faria o documento servir a dois locais, e aí ele é uma
 *   transferência seguida de dois consumos.
 */
export const itemConsumoEntradaSchema = z.object({
  /** Insumo consumido. Sempre material: serviço não movimenta estoque. */
  insumoId: uuidSchema,
  quantidade: quantidadeDoConsumoSchema,
  /**
   * Etapa ou bloco da obra, **por item**.
   *
   * No item, e não no cabeçalho, porque é o que costuma diferir entre as linhas
   * da mesma retirada: o cimento é da laje do 3º, o bloco é da alvenaria do 2º.
   * Uma etapa única obrigaria a abrir dois documentos para o mesmo carrinho de
   * material.
   *
   * Opcional, e **nula** quando não escolhida: nem toda obra tem etapas
   * cadastradas. Aceita `null` além de ausente porque o `<select>` do formulário
   * guarda a string vazia, que o schema de tela converte para `null` — recusar o
   * nulo obrigaria o formulário a apagar a chave do objeto antes de enviar, que
   * é o tipo de detalhe que se esquece numa das duas telas.
   */
  obraParteId: uuidSchema.nullable().prefault(null),
  /**
   * Onde este material foi aplicado, em texto livre. **Opcional.**
   *
   * A do item responde o que a do cabeçalho não alcança: "os dois sacos que
   * sobraram voltam amanhã". Vazia é o normal — a obra, a etapa e o tipo de
   * movimentação já dizem por que o material saiu.
   */
  observacao: z
    .string()
    .trim()
    .max(500, 'A observação do material tem no máximo 500 caracteres.')
    .prefault(''),
});

export type ItemConsumoEntrada = z.infer<typeof itemConsumoEntradaSchema>;

/**
 * A chave que não se repete dentro de uma requisição: **material + etapa**.
 *
 * No contrato, e não em cada ponta, porque três lugares a usam — o `superRefine`
 * deste arquivo, o card da tela de consumo e a distribuição —, e a cópia que
 * esquecesse a etapa recusaria a divisão que a regra passou a permitir.
 */
export function chaveDoItemDeConsumo(item: {
  insumoId: string;
  obraParteId?: string | null | undefined;
}): string {
  return `${item.insumoId}|${item.obraParteId ?? ''}`;
}

/** A recusa da linha repetida — a mesma frase na API e na tela. */
export const MENSAGEM_ITEM_DE_CONSUMO_REPETIDO =
  'Este material já está em outra linha com a mesma etapa. Some as quantidades numa linha só.';

/**
 * Um item como a API o devolve.
 *
 * O nome do insumo, a sigla da unidade e o nome da etapa vêm
 * **desnormalizados**: a linha mostra "Cimento CP-II · 12 SC · Laje do 3º" e não
 * dois uuid, e resolver isso no cliente significaria duas consultas por linha.
 *
 * `valorUnitario` e `valorTotal` são **gravados**, e não recalculados na leitura:
 * eles são o custo médio do instante da baixa, e o custo médio de hoje é outro
 * (ADR 0004 — fato gravado não se recalcula a partir de cadastro mutável). É a
 * diferença em relação ao item da lista de compra direta, cujo preço é previsão.
 */
export const itemConsumoSchema = z.object({
  id: uuidSchema,
  insumoId: uuidSchema,
  insumoNome: z.string().nullable(),
  /** Sigla da unidade do insumo — a linha escreve "12 SC", não "12". */
  insumoUnidade: z.string().nullable(),
  /** Decide se a quantidade se exibe com decimais. */
  insumoApenasInteiros: z.boolean(),
  obraParteId: uuidSchema.nullable(),
  obraParteNome: z.string().nullable(),
  /** Strings decimais, nunca number: ponto flutuante perde centavos em silêncio. */
  quantidade: quantidadeSchema,
  /**
   * O custo médio vigente no instante da baixa, calculado pelo servidor. Seis
   * casas, como o custo médio de onde ele vem — ver `custoUnitarioSchema`.
   */
  valorUnitario: custoUnitarioSchema,
  valorTotal: dinheiroSchema,
  observacao: z.string(),
});

export type ItemConsumo = z.infer<typeof itemConsumoSchema>;

/* -------------------------------------------------------------------------- */
/* Documento                                                                  */
/* -------------------------------------------------------------------------- */

const itensSchema = z
  .array(itemConsumoEntradaSchema)
  .min(1, 'O consumo precisa de pelo menos um material.')
  .max(MAXIMO_ITENS_CONSUMO, `O consumo tem no máximo ${MAXIMO_ITENS_CONSUMO} materiais.`);

/**
 * Payload de gravação.
 *
 * **Três ausências no cabeçalho**, além das três do item.
 *
 * - `tipo` não existe nem na tabela: consumo é **sempre** saída. Um campo que só
 *   admite um valor é convite a mandar o outro.
 * - `numero` é do contador da conta, atribuído na transação que cria o
 *   documento. Aceitá-lo do cliente deixaria dois consumos nascerem com o mesmo,
 *   e o segundo descobriria isso como violação de índice.
 * - `notaCompraItemId` é escrito pela confirmação da nota, e só por ela. Aceitá-lo
 *   aqui deixaria um consumo lançado à mão se declarar automático, e a exclusão
 *   da nota apagaria baixa que o almoxarife lançou.
 */
export const criarLancamentoConsumoSchema = z
  .object({
    /** Empresa do grupo — `pessoas` com `is_empresa`. É a dona do depósito. */
    empresaId: uuidSchema,
    /** Dia do consumo. Data de negócio, sem hora: o consumo é do dia 10. */
    dataLancamento: dataDoFatoSchema,
    /** Motivo do movimento. Precisa ser de sentido SAIDA — consumo não dá entrada. */
    tipoMovimentacaoEstoqueId: uuidSchema,
    /** A obra que recebeu o material. **Obrigatória** — é o que distingue consumo de baixa. */
    obraId: uuidSchema,
    /** Depósito de onde o material saiu. Um só, do cabeçalho. */
    depositoId: uuidSchema,
    /**
     * Motivo da retirada, em texto livre. **Opcional.**
     *
     * Era obrigatória com mínimo de três caracteres, copiada do lançamento de
     * estoque. A cópia não pensou na diferença: lá a baixa não tem documento
     * nenhum a explicá-la, e aqui a linha já traz a obra, a etapa e o tipo de
     * movimentação. O que sobrava era "Concretagem da laje" digitado quinze vezes
     * num lançamento de quinze materiais — e texto repetido por obrigação não se
     * lê depois. Com o documento de itens, a frase é escrita **uma vez**, que era
     * o que se queria desde o começo.
     */
    observacao: z
      .string()
      .trim()
      .max(1000, 'A observação tem no máximo 1000 caracteres.')
      .prefault(''),
    itens: itensSchema,
  })
  .superRefine((dados, ctx) => {
    /*
      O mesmo material **na mesma etapa** duas vezes no documento.

      O índice único recusaria de qualquer forma, com uma violação de constraint
      que o usuário não consegue interpretar. Aqui o erro aponta a **linha**, que
      é o que permite à tela destacá-la — e a correção é somar as duas
      quantidades numa só, não escolher qual apagar.

      A chave é o par **insumo + etapa** desde 25/09/2026. Até ali era o insumo
      sozinho, com o argumento de que a segunda linha do mesmo cimento "leria o
      custo médio que a primeira acabou de mexer". Não lê: a saída **não
      recalcula** o custo médio (`aplicarSaida`, em `@construtora/domain`), e a
      segunda baixa apenas encontra o saldo já reduzido pela primeira, que é o
      que deve encontrar. O custo daquela regra era real — o cimento dividido
      entre fundação e laje obrigava a abrir duas requisições —, e a distribuição
      de consumo (`distribuicao-consumo.ts`) o tornou o caso normal.

      Etapa vazia conta como **uma** etapa: duas linhas do mesmo material sem
      etapa são a mesma linha digitada duas vezes.
    */
    const vistos = new Set<string>();

    dados.itens.forEach((item, indice) => {
      const chave = chaveDoItemDeConsumo(item);
      if (vistos.has(chave)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['itens', indice, 'insumoId'],
          message: MENSAGEM_ITEM_DE_CONSUMO_REPETIDO,
        });
      }
      vistos.add(chave);
    });
  });

export type CriarLancamentoConsumo = z.infer<typeof criarLancamentoConsumoSchema>;

/**
 * O consumo como a API o devolve.
 *
 * Os nomes de empresa, tipo, depósito e obra vêm **desnormalizados**: a listagem
 * mostra "Almoxarifado Central · Residencial Aurora" e não quatro uuid, e
 * resolver isso no cliente significaria quatro consultas por linha.
 *
 * `notaCompraNumero` acompanha a origem `NOTA` pelo mesmo motivo prático: a
 * mensagem de recusa da exclusão precisa dizer **qual** nota desfazer, e mandar
 * o usuário procurar pelo id do item não é dizer.
 */
export const lancamentoConsumoSchema = z.object({
  id: uuidSchema,
  /**
   * O número sequencial do documento, **do servidor**.
   *
   * Único na conta inteira, independentemente da empresa do grupo: é o número da
   * requisição que o encarregado assina no almoxarifado e cita depois — "a 412
   * saiu completa?" —, e ele não deve exigir dizer também de qual empresa se
   * fala. Mesma regra da solicitação, do pedido e da transferência.
   *
   * O consumo **não tinha número** até 12/09/2026, e a razão registrada era que
   * ele não saía da tela de quem o digitava. Saía: com o documento de itens ele
   * virou a requisição de material, que é papel que circula.
   */
  numero: z.number().int(),
  empresaId: uuidSchema,
  empresaNome: z.string().nullable(),
  dataLancamento: dataSchema,
  tipoMovimentacaoEstoqueId: uuidSchema,
  tipoMovimentacaoEstoqueNome: z.string().nullable(),
  depositoId: uuidSchema,
  depositoNome: z.string().nullable(),
  obraId: uuidSchema,
  obraNome: z.string().nullable(),
  origem: origemConsumoSchema,
  /** Preenchido só na origem `NOTA`. Ver `consumoExcluivel`. */
  notaCompraItemId: uuidSchema.nullable(),
  notaCompraNumero: z.string().nullable(),
  observacao: z.string(),
  /** Soma do `valorTotal` dos itens — o custo que entrou na obra por este documento. */
  valorTotal: dinheiroSchema,
  itens: z.array(itemConsumoSchema),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type LancamentoConsumo = z.infer<typeof lancamentoConsumoSchema>;

export const listaLancamentosConsumoSchema = respostaListaSchema(lancamentoConsumoSchema);
export type ListaLancamentosConsumo = z.infer<typeof listaLancamentosConsumoSchema>;

/**
 * A soma dos itens, a centavo.
 *
 * `BigInt` sobre centavos, e não `Number`: ponto flutuante perde centavo em
 * silêncio (ADR 0003). Os totais de linha já vêm gravados com duas casas, então
 * basta somar os inteiros escalados.
 *
 * No contrato, e não só no repositório, porque a tela soma a mesma coisa
 * enquanto o usuário digita — e duas implementações discordam num arredondamento.
 */
export function totalDoConsumo(itens: readonly { valorTotal: string }[]): string {
  const centavos = itens.reduce(
    (soma, item) => soma + BigInt(item.valorTotal.replace('.', '')),
    0n,
  );

  return `${centavos / 100n}.${String(centavos % 100n).padStart(2, '0')}`;
}

/**
 * Filtros da listagem.
 *
 * Sem `ativo`: consumo é documento de movimento, não cadastro — ele existe ou
 * não existe. O recorte natural é **obra e período** ("o que a Aurora consumiu
 * em julho"), que é a pergunta que a tabela existe para responder e é exatamente
 * o índice que ela tem.
 *
 * `insumoId` e `obraParteId` recortam por **item**, e continuam aqui: "quais
 * requisições levaram cimento" é a pergunta de quem confere o consumo de um
 * material. A consulta os resolve com um `EXISTS` sobre os itens, e não com uma
 * junção, para não multiplicar a linha do documento e estragar a paginação.
 */
export const listarLancamentosConsumoQuerySchema = paginacaoSchema.extend({
  empresaId: uuidSchema.optional(),
  obraId: uuidSchema.optional(),
  obraParteId: uuidSchema.optional(),
  depositoId: uuidSchema.optional(),
  insumoId: uuidSchema.optional(),
  tipoMovimentacaoEstoqueId: uuidSchema.optional(),
  origem: origemConsumoSchema.optional(),
  /** Início do período, inclusivo. */
  de: dataSchema.optional(),
  /** Fim do período, inclusivo. */
  ate: dataSchema.optional(),
});

export type ListarLancamentosConsumoQuery = z.infer<typeof listarLancamentosConsumoQuerySchema>;

/**
 * As colunas ordenáveis.
 *
 * **Sem `quantidade` e sem `valorTotal`**, que existiam quando o documento era
 * uma linha só. Um documento de quinze materiais não tem "a" quantidade, e
 * ordenar por ela exigiria escolher em silêncio uma das quinze. O total existe,
 * mas é a **soma dos itens** e não uma coluna: ordenar por ele obrigaria a
 * consulta da página a somar a tabela filha antes de paginar, que é o trabalho
 * mais caro da tela feito pelo critério menos usado. Quem procura o consumo caro
 * filtra por obra e período, e olha a coluna.
 */
export const COLUNAS_ORDENAVEIS_LANCAMENTO_CONSUMO = [
  'numero',
  'dataLancamento',
  'createdAt',
] as const;
